import { random } from 'underscore'
import { ReactiveBridge } from '@/common/model/'
import Events from '@/configuration/Events'

/**
 * Used to access CardForm instance by label instead of searching for element
 * in DOM.
 * Originally added to access instance from KryptonPopinButton.
 *
 * @since KJS-4240
 */
export default class CardFormController {
  constructor({ $bus, $store, ghostQueue, storeFactory }) {
    this.$bus = $bus
    this.$store = $store
    this.storeFactory = storeFactory
    this.ghostQueue = ghostQueue
    this.cardFormComp = new Map()
    this.bridge = new ReactiveBridge(this)
    this.bridge
      .store($store)
      .state(['dna', 'forms'])
      .computed({
        hasAddedDefaultForm() {
          return typeof this.forms.default === 'string'
        }
      })
      .actions(['addForm', 'registerCardFormModule', 'renderCardForm'])
      .build()
  }

  /**
   * Register CardForm Vue instance to give access everywhere.
   * This allows access its properties the right way, not running a DOM query.
   * Called by CardForm instance at creation.
   *
   * @param {VueComponent} form CardForm instance
   */
  registerForm(form) {
    const id = form.context.formId
    this.cardFormComp.set(id, form)
  }

  /**
   * @param {string} label Card Form label as used in $store.state.forms
   * @returns {Proxy & { getProperty: Function }} CardForm Proxy to limit accessible properties
   */
  getByLabel(label) {
    const id = this.forms[label]
    return this.cardFormComp.get(id).proxy()
  }

  /**
   * @param {string} id Card Form ID
   * @returns {Proxy & { getProperty: Function }} CardForm Proxy to limit accessible properties
   */
  getById(id) {
    return this.cardFormComp.get(id).proxy()
  }

  /**
   * Moved from DNA service and generalized to be used for both default and
   * additional forms.
   *
   * @param {string} section section name matching the spawn point
   * @returns {string} newly created form id
   * @since KJS-4782
   */
  createForm(section = 'default') {
    // Create form only if it is not already created
    if (section === 'default' && this.hasAddedDefaultForm) {
      return this.forms.default
    }

    // Adding a new form will make the application not be synchronized again
    this.$store.dispatch('update', { synced: false })

    const formId = `F${random(10000, 99999)}`
    const store = this.storeFactory.create('actionNewForm', {
      formId,
      dna: this.dna
    })

    this.addForm({ label: section, id: formId })
    this.registerCardFormModule({ id: formId })

    if (section === 'ILLICADO')
      this.$store.dispatch(
        `cardForm_${this.$store.state.forms[section]}/update`,
        {
          forcedBrand: section
        }
      )

    this.renderCardForm(formId)
    this.ghostQueue.send(store)
    this.$bus.$emit(Events.krypton.form.new, { formId })
    return formId
  }
}
